import { useState } from 'react';
import { StatWidget } from '@/components/widgets/StatWidget';
import { formatNumber } from '@/utils/format';
import { BreakdownCard, type BreakdownTable } from '../dailyReconciliation/clientsAssets/BreakdownCard';
import type {
  PortfolioEquityConnectorTotalDto,
  PortfolioEquityCurrencyTotalDto,
  PortfolioEquityLpTotalDto,
  PortfolioEquityMetricDto,
  PortfolioEquitySummaryDto,
} from '@/api/types/portfolioEquity';

const n = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '0.00' : formatNumber(v, 2));

type MetricKey = 'equity' | 'balance' | 'floating';

interface SummaryTabProps {
  summary?: PortfolioEquitySummaryDto;
  isLoading: boolean;
}

// StatWidget theme per legacy metric colour: Equity=Primary, Balance=Info,
// Floating=neutral (legacy Secondary → no-secondary rule).
const WIDGETS: { key: MetricKey; label: string; icon: string; theme: 'primary' | 'info' | 'neutral' }[] = [
  { key: 'equity', label: 'Equity (RC)', icon: 'fa-solid fa-chart-line', theme: 'primary' },
  { key: 'balance', label: 'Balance (RC)', icon: 'fa-solid fa-sack-dollar', theme: 'info' },
  { key: 'floating', label: 'Floating/UnrealizedPnL (RC)', icon: 'fa-solid fa-right-left', theme: 'neutral' },
];

export function SummaryTab({ summary, isLoading }: SummaryTabProps) {
  const [open, setOpen] = useState<MetricKey | null>(null);
  const toggle = (p: MetricKey) => setOpen((cur) => (cur === p ? null : p));

  return (
    <div>
      <div className="row g-2">
        {WIDGETS.map((w) => (
          <div className="col-md-4" key={w.key}>
            <StatWidget
              label={w.label}
              value={n(summary?.[w.key]?.total)}
              icon={w.icon}
              theme={w.theme}
              onClick={() => toggle(w.key)}
              active={open === w.key}
            />
          </div>
        ))}
      </div>

      {isLoading && <div className="text-muted mt-3">Loading…</div>}

      {open && summary && <Breakdown metric={summary[open]} label={WIDGETS.find((w) => w.key === open)!.label} />}
    </div>
  );
}

function Breakdown({ metric, label }: { metric?: PortfolioEquityMetricDto; label: string }) {
  if (!metric) return null;

  const tables: BreakdownTable<unknown>[] = [
    {
      title: `${label} By Connector`,
      columns: [
        { header: 'Connector', cell: (r) => (r as PortfolioEquityConnectorTotalDto).connector },
        { header: 'Total', className: 'text-end', cell: (r) => n((r as PortfolioEquityConnectorTotalDto).total) },
      ],
      rows: metric.byConnector ?? [],
    },
  ];

  if (metric.byLp && metric.byLp.length > 0) {
    tables.push({
      title: `${label} By LPs`,
      columns: [
        { header: 'LP', cell: (r) => (r as PortfolioEquityLpTotalDto).lp },
        { header: 'Total', className: 'text-end', cell: (r) => n((r as PortfolioEquityLpTotalDto).total) },
      ],
      rows: metric.byLp,
    });
  }

  tables.push({
    title: `${label} By Currency`,
    columns: [
      { header: 'Acc Currency', cell: (r) => (r as PortfolioEquityCurrencyTotalDto).currency },
      { header: 'Total', className: 'text-end', cell: (r) => n((r as PortfolioEquityCurrencyTotalDto).total) },
    ],
    rows: metric.byCurrency ?? [],
  });

  return <BreakdownCard tables={tables} />;
}
